import type { ClinicNAP } from "@/lib/clinic";

interface Props {
  clinic: ClinicNAP;
  variant: "compact" | "full";
  className?: string;
}

const telOf = (clinic: ClinicNAP) =>
  `tel:${clinic.phone.international.replace(/\s+/g, "")}`;

const mapsOf = (clinic: ClinicNAP) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${clinic.name} ${clinic.address.street} ${clinic.address.postalCode}`,
  )}`;

/**
 * Stateless mirror of <ClinicNAP/>: renders identical markup but reads
 * from a passed-in clinic object instead of the live store. Used by the
 * settings panel preview so admins can see header + footer NAP changes
 * before saving.
 */
const ClinicNAPPreview = ({ clinic, variant, className = "" }: Props) => {
  const telHref = telOf(clinic);
  const mapsHref = mapsOf(clinic);

  if (variant === "compact") {
    return (
      <div
        className={`flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-muted-foreground ${className}`}
      >
        <span className="hidden lg:inline">
          <span>{clinic.address.street}</span>
          <span className="opacity-50 mx-2">·</span>
          <span>{clinic.address.postalCode}</span>
        </span>
        <a href={telHref} className="hover:text-accent transition-colors whitespace-nowrap">
          {clinic.phone.display}
        </a>
      </div>
    );
  }

  return (
    <div className={`space-y-5 ${className}`}>
      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Visit</p>
        <p className="font-display text-lg leading-snug">{clinic.name}</p>
        <address className="not-italic font-light opacity-90 leading-relaxed mt-1">
          {clinic.address.street}
          <br />
          {clinic.address.locality}, {clinic.address.region} {clinic.address.postalCode}
          <br />
          {clinic.address.country}
        </address>
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-3 text-[10px] tracking-[0.3em] uppercase hover:text-accent transition-colors opacity-70"
        >
          Open in Maps →
        </a>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Call</p>
        <a href={telHref} className="block font-light opacity-90 hover:text-accent transition-colors">
          {clinic.phone.display}
        </a>
        {clinic.whatsapp.url && (
          <a
            href={clinic.whatsapp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-1 font-light opacity-90 hover:text-accent transition-colors"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 mr-2">WhatsApp</span>
            {clinic.whatsapp.display}
          </a>
        )}
        <p className="font-light opacity-70 text-sm mt-2">{clinic.bookingNote}</p>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Hours</p>
        <ul className="space-y-1 text-sm font-light opacity-90">
          {clinic.hours.map((row, i) => (
            <li key={`${row.days}-${i}`} className="flex justify-between gap-6">
              <span>{row.days}</span>
              <span className="opacity-80">{row.time}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ClinicNAPPreview;
